/**
 * Who is using this browser, for `<IdentityBar>`. Two independent identities: an account
 * (`HttpOnly` cookie, only knowable via `/auth/me`) and a player (per-code `localStorage`).
 * A store, because both change from places the bar cannot see. The account is cached and
 * invalidated only by sign-in/sign-out, which both go through here.
 */

import { authApi, type AccountSummary } from './auth';
import { loadPlayer, type StoredPlayer } from './storage';

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * What `useSyncExternalStore` reads. A counter, not the identities: a fresh `JSON.parse`
 * object would look new on every render and loop.
 */
let version = 0;

function notify(): void {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getVersion(): number {
  return version;
}

// ---------------------------------------------------------------------------
// The account half
// ---------------------------------------------------------------------------

/**
 * `undefined` = not asked yet, `null` = nobody signed in. Stops the bar flashing
 * "Not signed in" during the first round trip.
 */
let account: AccountSummary | null | undefined;
let inFlight: Promise<void> | null = null;

export function accountNow(): AccountSummary | null | undefined {
  return account;
}

/** Ask the server once. Repeat calls while one is in flight join it. */
export function loadAccount(): Promise<void> {
  if (account !== undefined) return Promise.resolve();
  inFlight ??= authApi
    .me()
    .then((res) => {
      account = res.account;
    })
    .catch(() => {
      // Unreachable server: "not signed in" is the safe thing to draw.
      account = null;
    })
    .finally(() => {
      inFlight = null;
      notify();
    });
  return inFlight;
}

/** `AuthGate` calls this the moment a sign-in or a registration lands. */
export function noteSignedIn(next: AccountSummary): void {
  account = next;
  notify();
}

/** Sign out for real, then tell every subscriber. */
export async function signOut(): Promise<void> {
  try {
    await authApi.logout();
  } finally {
    // Cleared even on failure: claiming an account after a failed logout is worse.
    account = null;
    notify();
  }
}

// ---------------------------------------------------------------------------
// The player half
// ---------------------------------------------------------------------------

/**
 * The name this browser plays under in one session. From `localStorage` because the bar
 * has no socket; `notePlayerChanged` keeps it fresh.
 */
export function playerIn(code: string | null): StoredPlayer | null {
  return code ? loadPlayer(code) : null;
}

/** The player shell calls this after `savePlayer` or `clearPlayer`. */
export function notePlayerChanged(): void {
  notify();
}
